import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { trackRouteFallback } from '@/lib/analytics';
import { isPrefetchedPath } from '@/utils/route-prefetch';
import {
  markSuspenseStart,
  markSuspenseEnd,
  getActiveCacheHit,
} from '@/lib/route-timing';

/**
 * Wraps a Suspense fallback to time how long it stays mounted.
 *
 * - Mounts when React begins rendering a lazy chunk that isn't ready.
 * - Unmounts as soon as that chunk resolves and the real route renders.
 *
 * On unmount we emit a `route_fallback` analytics event with the path,
 * the visible duration in milliseconds, and whether the path was on the
 * prefetch warm-list (so we can verify prefetch is doing its job).
 *
 * Pure instrumentation — no behavior change.
 */
export default function FallbackTimer({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const startedAt = useRef<number>(
    typeof performance !== 'undefined' ? performance.now() : Date.now(),
  );

  useEffect(() => {
    const path = pathname;
    markSuspenseStart(path);
    return () => {
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      const durationMs = now - startedAt.current;
      const prefetched = isPrefetchedPath(path);
      // Capture the cacheHit flag recorded at navigation start. If the
      // active timing record has already rotated (e.g. the user navigated
      // again before this fallback unmounted), fall back to false — by
      // definition the chunk wasn't ready since we showed a fallback.
      const cacheHit = getActiveCacheHit(path) ?? false;
      markSuspenseEnd(path);

      // Console log gives us cheap visibility in dev/preview without a
      // configured analytics backend.
      if (typeof console !== 'undefined') {
        console.info(
          '[route-fallback]',
          JSON.stringify({
            path,
            durationMs: Math.round(durationMs),
            prefetched,
            cacheHit,
          }),
        );
      }

      try {
        trackRouteFallback(path, durationMs, prefetched, cacheHit);
      } catch {
        /* never throw from instrumentation */
      }
    };
    // We intentionally only run once per mount; pathname captured above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}